import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useIsMembershipActive } from "../../../contexts/MembershipContext";
import { api } from "../../../singleton/axios";
import type { ScreenerAllowance } from "../types";

import { screenerUsageQueryKey } from "./queryKeys";
import {
  SCREENER_READ_TIMEOUT_MS,
  screenerRetry,
  screenerRetryDelay,
} from "./screenerRetry";

export { screenerUsageQueryKey };

// setTimeout's longest delay; a reset further off is caught by a later read.
const MAX_TIMER_MS = 2_147_483_647;

/**
 * Tier, used, limit and the reset for the allowance copy; refreshed on focus,
 * and at the UTC month rollover itself, so a tab left open across it shows the
 * new month's allowance.
 */
export function useScreenerUsage() {
  const { isMembershipActive } = useIsMembershipActive();
  const queryClient = useQueryClient();
  const query = useQuery<ScreenerAllowance, Error>({
    queryKey: screenerUsageQueryKey,
    queryFn: async () => {
      const { data } = await api.get<ScreenerAllowance>("/screener/usage", {
        timeout: SCREENER_READ_TIMEOUT_MS,
      });
      return data;
    },
    enabled: isMembershipActive,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    retry: screenerRetry,
    retryDelay: screenerRetryDelay,
  });
  const resetInMs = query.data?.resetInMs;
  const fetchedAt = query.dataUpdatedAt;
  useEffect(() => {
    if (resetInMs === undefined) return;
    const due = fetchedAt + resetInMs + 1000 - Date.now();
    if (due > MAX_TIMER_MS) return;
    const timer = setTimeout(
      () => queryClient.invalidateQueries({ queryKey: screenerUsageQueryKey }),
      Math.max(0, due),
    );
    return () => clearTimeout(timer);
  }, [resetInMs, fetchedAt, queryClient]);
  return query;
}
